import { Controller } from "@hotwired/stimulus"
import mapboxgl from "mapbox-gl"
import CENTROIDS from "../data/country_centroids"

// Carte monde plate (Mercator). Choropleth : chaque pays visité est rempli
// avec une teinte verte plus ou moins foncée selon le nombre de visites
// (échelle racine carrée). Clic = zoom auto sur le pays. Hover = tooltip.
//
// Source des frontières : mapbox.country-boundaries-v1 (gratuit). Les codes
// pays Mapbox sont en MAJUSCULES (iso_3166_1), ceux de Matomo en minuscules :
// on normalise en majuscules côté JS.
export default class extends Controller {
  static targets = ["map"]
  static values = {
    apiKey: String,
    data: Array
  }

  connect() {
    if (!this.apiKeyValue || !this.hasMapTarget) return

    mapboxgl.accessToken = this.apiKeyValue
    this.map = new mapboxgl.Map({
      container: this.mapTarget,
      style: "mapbox://styles/mapbox/light-v11",
      projection: "mercator",
      center: [10, 25],
      zoom: 1.2,
      minZoom: 1,
      attributionControl: false
    })

    this.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right")
    this.map.addControl(new mapboxgl.FullscreenControl(), "top-right")
    this.map.addControl(new mapboxgl.AttributionControl({ compact: true }))

    this.map.on("load", () => {
      this.addChoropleth()
      this.wireInteractions()
    })
  }

  disconnect() {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  // Index { 'FR': 50, 'DJ': 30, ... } depuis les données Matomo.
  visitsByCountry() {
    const out = {}
    for (const row of (this.dataValue || [])) {
      const code = String(row.code || "").toUpperCase()
      if (!code) continue
      out[code] = Number(row.nb_visits) || 0
    }
    return out
  }

  addChoropleth() {
    const visits = this.visitsByCountry()
    const codes = Object.keys(visits)
    if (codes.length === 0) return

    const max = Math.max(...Object.values(visits), 1)

    // Expression match : par pays, calcule une couleur RGBA via interpolate.
    // `match` exige paire(s) (clé, valeur) puis un fallback.
    // On utilise `interpolate` séparément pour transformer ratio → couleur.
    const fillMatch = ["match", ["upcase", ["get", "iso_3166_1"]]]
    for (const code of codes) {
      const ratio = Math.sqrt(visits[code]) / Math.sqrt(max)
      fillMatch.push(code, this.greenAt(ratio))
    }
    fillMatch.push("rgba(0,0,0,0)") // pays non visités : transparent

    this.map.addSource("country-boundaries", {
      type: "vector",
      url: "mapbox://mapbox.country-boundaries-v1"
    })

    this.map.addLayer({
      id: "country-fills",
      type: "fill",
      source: "country-boundaries",
      "source-layer": "country_boundaries",
      paint: {
        "fill-color": fillMatch,
        "fill-opacity": 0.75
      }
    }, this.firstLabelLayerId())

    this.map.addLayer({
      id: "country-borders",
      type: "line",
      source: "country-boundaries",
      "source-layer": "country_boundaries",
      paint: {
        "line-color": "#214F18",
        "line-width": ["case", ["boolean", ["feature-state", "hover"], false], 1.5, 0.4],
        "line-opacity": 0.7
      }
    }, this.firstLabelLayerId())
  }

  // Place le layer juste sous la première couche de labels pour que les noms
  // de pays restent lisibles par-dessus.
  firstLabelLayerId() {
    const layers = this.map.getStyle().layers || []
    const labelLayer = layers.find(l => l.type === "symbol" && l.layout && l.layout["text-field"])
    return labelLayer && labelLayer.id
  }

  // Interpole entre un vert clair et le vert ARKO bgmembre.
  greenAt(ratio) {
    const t = Math.max(0, Math.min(1, ratio))
    // light: #d9e8d3 -> dark: #214F18
    const lerp = (a, b) => Math.round(a + (b - a) * t)
    const r = lerp(0xd9, 0x21)
    const g = lerp(0xe8, 0x4f)
    const b = lerp(0xd3, 0x18)
    return `rgb(${r}, ${g}, ${b})`
  }

  wireInteractions() {
    const visits = this.visitsByCountry()
    const popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false })

    let hoveredId = null
    const setHover = (id, on) => {
      if (id == null) return
      this.map.setFeatureState(
        { source: "country-boundaries", sourceLayer: "country_boundaries", id },
        { hover: on }
      )
    }

    this.map.on("mousemove", "country-fills", (e) => {
      if (!e.features.length) return
      const f = e.features[0]
      const code = String(f.properties.iso_3166_1 || "").toUpperCase()
      const count = visits[code] || 0
      if (count === 0) {
        popup.remove()
        return
      }

      if (hoveredId !== f.id) {
        setHover(hoveredId, false)
        hoveredId = f.id
        setHover(hoveredId, true)
      }

      this.map.getCanvas().style.cursor = "pointer"
      const label = f.properties.name_en || code
      popup
        .setLngLat(e.lngLat)
        .setHTML(`<strong>${label}</strong><br>${count.toLocaleString("fr-FR")} visites`)
        .addTo(this.map)
    })

    this.map.on("mouseleave", "country-fills", () => {
      this.map.getCanvas().style.cursor = ""
      setHover(hoveredId, false)
      hoveredId = null
      popup.remove()
    })

    // Clic sur un pays visité → zoom sur le centroïde du pays
    this.map.on("click", "country-fills", (e) => {
      if (!e.features.length) return
      const code = String(e.features[0].properties.iso_3166_1 || "").toLowerCase()
      if (!visits[code.toUpperCase()]) return
      const centroid = CENTROIDS[code]
      if (centroid) {
        this.map.flyTo({ center: centroid, zoom: 4.5, duration: 800 })
      }
    })
  }
}
